import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { Fragment, useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import {
  categoryDelete,
  categoryList,
  categoryOrdering,
  categoryStatusChange,
} from "../../../../services/categoryService";
import ManageCategoryDrawer from "../../../../components/sections/admin/Category/Drawer/ManageCategoryDrawer";
import ManageSubCategoryModal from "../../../../components/sections/admin/Category/Modal/ManageSubCategory";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import PopoverDropdown from "../../../../components/elements/PopoverDropdown";
import { t } from "i18next";

const statusList = [
  { name: "Active", value: "Active", type: "checkbox" },
  { name: "Inactive", value: "Inactive", type: "checkbox" },
];

const Category = () => {
  setTitle("Stackora Admin | Category");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [subModalOpen, setSubModalOpen] = useState({
    isOpen: false,
    data: null,
    index: 0,
  });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["category", filters, search],
    queryFn: async () => {
      const res = await categoryList({
        ...filters,
        type: "event-category",
        ...(search && { keyword: search }),
      });

      if (res?.docs?.status !== 200) {
        toast.warning(res?.message);
      }

      return res?.docs;
    },
  });

  //   organization status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(["category", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) =>
              elm?._id === id
                ? {
                    ...elm,
                    status: status,
                  }
                : elm
            );
            return { ...state, data: stateData };
          }
        });
        const res = await categoryStatusChange({
          ids: [id],
          status: status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          queryClient.setQueryData(["category", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === payload?.id
                  ? {
                      ...elm,
                      status: payload?.status,
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  // Recursive Bullate
  const renderNestedListWithLimit = (items, catIndex, limit = 3) => {
    const visibleItems = items.slice(0, limit);
    const remainingCount = items.length - limit;

    return (
      <Fragment>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          {visibleItems.map((item, i) => (
            <li key={i}>
              {lngDetect(item.name)}
              {item.ancestors && renderNestedList(item.ancestors)}
              {i === limit - 1 &&
                remainingCount > 0 &&
                ` +${remainingCount} more`}
            </li>
          ))}
        </ul>
        {/* {visibleItems.length > 0 && (
          <Button
            buttonLabel="Manage"
            buttonClasses={"!h-8"}
            buttonLabelClasses={"!text-sm"}
            buttonFunction={() =>
              setSubModalOpen({
                isOpen: true,
                data: data?.data[catIndex],
                index: catIndex,
              })
            }
          />
        )} */}
      </Fragment>
    );
  };

  const renderNestedList = (items) => {
    return (
      <ul className="list-disc pl-5 space-y-1 text-sm">
        {items.map((item, i) => (
          <li key={i}>
            {lngDetect(item.name)}
            {item.children && renderNestedList(item.children)}
          </li>
        ))}
      </ul>
    );
  };

  const tableHeadData = [
    {
      _id: 1,
      name: "Category",
      align: "left",
    },
    {
      _id: 2,
      name: "Sub Category",
      align: "left",
    },
    {
      _id: 3,
      name: "Description",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: statusList,
      }),
    },
    {
      _id: 5,
      name: "Created",
      align: "left",
    },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item, index) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.name) || "-",
          image: item?.media?.image?.url,
          isTooltip: false,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: renderNestedListWithLimit(item.ancestorsGraph, index) || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.description?.length > 25
              ? `${lngDetect(item.description).slice(0, 25)}...`
              : lngDetect(item?.description) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div>
              <PopoverDropdown
                buttonClass={"p-0 bg-transparent"}
                buttonLabel={
                  <Badge
                    label={item?.status}
                    className={"capitalize"}
                    type={item?.status === "active" ? "success" : "danger"}
                  />
                }
              >
                <ul>
                  {statusList?.map((status, i) => (
                    <li
                      key={i?.toString()}
                      className="hover:text-white px-2 py-2 hover:bg-gray-700/40 rounded-md"
                    >
                      <button
                        className="flex items-center justify-start w-full h-full"
                        type="button"
                        onClick={() => {
                          statusChangeHandler({
                            status: status?.value,
                            id: item?._id,
                          });
                        }}
                      >
                        {status?.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverDropdown>
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 9.1,
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: 9.2,
              name: t("Delete"),
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      refetch();
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   organization delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await categoryDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["category", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.filter(
              (item) => !payload?.includes(item?._id)
            );

            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["category", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await categoryOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["category", filters, search], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col bg-white rounded-xl p-5">
      <div className="space-y-8">
        <div className="font-bold text-lg xl:text-3xl">Category Management</div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} Selected
                <button
                  onClick={() => setSelectedItems([])}
                  type="button"
                  className="cursor-pointer"
                >
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
          <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              buttonLabel={"Create New"}
              variant="secondary"
              buttonLabelClasses={"font-medium !text-sm text-primary"}
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(["category", filters, search], (state) => {
            if (state) {
              return { ...state, data: val };
            }
          });
        }}
        handleOrdering={(newData, oldData) =>
          handleOrdering({ newData, oldData })
        }
        isOrderingDisabled={false}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        // selectable
      />
      <ManageCategoryDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={pageManageResponse}
      />
      <ManageSubCategoryModal
        open={subModalOpen?.isOpen}
        data={subModalOpen?.data}
        close={() => setSubModalOpen({ isOpen: false, data: null, index: 0 })}
        getResponse={pageManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Category;
